import axiosInstance from '../../../setup/axios/axiosInstance';
import {
  ICreateTransactionTemplate,
  IPagedResult,
  ITransactionExecution,
  ITransactionExecutionSummary,
  ITransactionSearchFilters,
  ITransactionStatistics,
  ITransactionParameter,
  ITransactionSummary,
  ITransactionTemplate,
  TemplateDirection,
} from './types';

const BASE = '/api/transactionexplorer';

const buildSearchQuery = (filters: ITransactionSearchFilters): string => {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.className) params.set('className', filters.className);
  if (filters.status !== undefined && filters.status !== null)
    params.set('status', String(filters.status));
  if (filters.username) params.set('username', filters.username);
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 10));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.order) params.set('order', filters.order);
  return params.toString();
};

export const searchTransactions = async (
  filters: ITransactionSearchFilters
): Promise<IPagedResult<ITransactionSummary>> => {
  const response = await axiosInstance.get(`${BASE}/search?${buildSearchQuery(filters)}`);
  return response.data as IPagedResult<ITransactionSummary>;
};

export const getStatistics = async (
  className: string,
  days: number = 30
): Promise<ITransactionStatistics> => {
  const response = await axiosInstance.get(
    `${BASE}/${encodeURIComponent(className)}/statistics?days=${days}`
  );
  return response.data as ITransactionStatistics;
};

export const getTemplates = async (className: string): Promise<ITransactionTemplate[]> => {
  const response = await axiosInstance.get(`${BASE}/${encodeURIComponent(className)}/templates`);
  // A failed upstream call answers 204, which axios surfaces as "" - not null - so `?? []`
  // would let a string through and blow up on .filter(). Only ever hand back an array.
  return Array.isArray(response.data) ? (response.data as ITransactionTemplate[]) : [];
};

export const setTemplatePinned = async (id: number, pinned: boolean): Promise<void> => {
  await axiosInstance.post(`${BASE}/templates/${id}/pin?pinned=${pinned}`);
};

export const addTemplate = async (
  className: string,
  direction: TemplateDirection,
  payload: string
): Promise<ITransactionTemplate> => {
  const body: ICreateTransactionTemplate = { direction, payload };
  const response = await axiosInstance.post(
    `${BASE}/${encodeURIComponent(className)}/templates`,
    body
  );
  return response.data as ITransactionTemplate;
};

export const getExecutions = async (
  className: string,
  take: number = 25
): Promise<ITransactionExecutionSummary[]> => {
  const response = await axiosInstance.get(
    `${BASE}/${encodeURIComponent(className)}/executions?take=${take}`
  );
  // Tolerate either a plain array or a paged result ({ items: [...] }).
  const data = response.data;
  if (Array.isArray(data)) return data as ITransactionExecutionSummary[];
  return (data?.items ?? data?.Items ?? []) as ITransactionExecutionSummary[];
};

export const getParameters = async (className: string): Promise<ITransactionParameter[]> => {
  const response = await axiosInstance.get(
    `${BASE}/${encodeURIComponent(className)}/parameters`
  );
  return Array.isArray(response.data) ? (response.data as ITransactionParameter[]) : [];
};

export const getExecution = async (correlationId: string): Promise<ITransactionExecution> => {
  const response = await axiosInstance.get(`${BASE}/execution/${encodeURIComponent(correlationId)}`);
  return response.data as ITransactionExecution;
};
